import React, { createContext, useContext, useReducer } from "react";

import { CartItem } from "../../api/fetchCart/types";

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ShippingAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

interface ProductCheckoutState {
  currentStep: number;
  customerInfo: CustomerInfo;
  shippingAddress: ShippingAddress;
  orderNotes: string;
  cartItems: CartItem[];
}

type ProductCheckoutAction =
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "GO_TO_STEP"; step: number }
  | { type: "SET_CUSTOMER_INFO"; data: CustomerInfo }
  | { type: "SET_SHIPPING_ADDRESS"; data: ShippingAddress }
  | { type: "SET_ORDER_NOTES"; notes: string }
  | { type: "SET_CART_ITEMS"; items: CartItem[] };

const MAX_STEP = 1;

const initialState: ProductCheckoutState = {
  currentStep: 0,
  customerInfo: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  },
  shippingAddress: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  orderNotes: "",
  cartItems: [],
};

function productCheckoutReducer(
  state: ProductCheckoutState,
  action: ProductCheckoutAction,
): ProductCheckoutState {
  switch (action.type) {
    case "NEXT_STEP":
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, MAX_STEP),
      };
    case "PREV_STEP":
      return { ...state, currentStep: Math.max(state.currentStep - 1, 0) };
    case "GO_TO_STEP":
      return {
        ...state,
        currentStep: Math.min(Math.max(action.step, 0), MAX_STEP),
      };
    case "SET_CUSTOMER_INFO":
      return { ...state, customerInfo: action.data };
    case "SET_SHIPPING_ADDRESS":
      return { ...state, shippingAddress: action.data };
    case "SET_ORDER_NOTES":
      return { ...state, orderNotes: action.notes };
    case "SET_CART_ITEMS":
      return { ...state, cartItems: action.items };
    default:
      return state;
  }
}

interface ProductCheckoutContextType {
  state: ProductCheckoutState;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  setCustomerInfo: (data: CustomerInfo) => void;
  setShippingAddress: (data: ShippingAddress) => void;
  setOrderNotes: (notes: string) => void;
  setCartItems: (items: CartItem[]) => void;
}

const ProductCheckoutContext = createContext<
  ProductCheckoutContextType | undefined
>(undefined);

export function useProductCheckout(): ProductCheckoutContextType {
  const context = useContext(ProductCheckoutContext);
  if (!context) {
    throw new Error(
      "useProductCheckout must be used within a ProductCheckoutProvider",
    );
  }
  return context;
}

export function ProductCheckoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(productCheckoutReducer, initialState);

  const nextStep = () => dispatch({ type: "NEXT_STEP" });
  const prevStep = () => dispatch({ type: "PREV_STEP" });
  const goToStep = (step: number) => dispatch({ type: "GO_TO_STEP", step });
  const setCustomerInfo = (data: CustomerInfo) =>
    dispatch({ type: "SET_CUSTOMER_INFO", data });
  const setShippingAddress = (data: ShippingAddress) =>
    dispatch({ type: "SET_SHIPPING_ADDRESS", data });
  const setOrderNotes = (notes: string) =>
    dispatch({ type: "SET_ORDER_NOTES", notes });
  const setCartItems = (items: CartItem[]) =>
    dispatch({ type: "SET_CART_ITEMS", items });

  return (
    <ProductCheckoutContext.Provider
      value={{
        state,
        nextStep,
        prevStep,
        goToStep,
        setCustomerInfo,
        setShippingAddress,
        setOrderNotes,
        setCartItems,
      }}
    >
      {children}
    </ProductCheckoutContext.Provider>
  );
}
